import { useEffect, useRef } from 'react';
import { useDebounce } from 'react-use';
import { AUTOSUGGEST_THRESHOLD, SEARCH_FLYOUT_DEBOUNCE } from '../../../tools/constants';
import { useSuggestions } from '../../SearchWrapper/hooks/useQueries';

/**
 * Custom hook for handling autosuggest functionality in a search input.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Function} params.blurHandler - Function to handle blur events (closing the dropdown).
 * @param {string} params.searchTerm - The current search term entered by the user.
 * @param {boolean} params.isFocused - Indicates if the search input is focused on the search bar.
 * @returns {Object} An object containing:
 *   - displayedData {Array}: Array of suggestions to display based on the search term.
 *   - keyDownHandler {Function}: Handler for keyboard events (Escape, Enter) to trigger blur.
 */

export default function useAutoSuggest({
  blurHandler,
  searchTerm,
  editableSearchTerm,
  isFocused,
}) {
  const { getSuggestions, suggestions } = useSuggestions();
  const { combinedSuggestions } = suggestions;
  const suggestionsVisible = editableSearchTerm.trim().length >= AUTOSUGGEST_THRESHOLD && isFocused;
  const displayedData = suggestionsVisible ? combinedSuggestions : [];
  const prevFocusedRef = useRef(false);

  // Immediate call when user first focuses and has existing search term that meets threshold
  useEffect(() => {
    // Only call when focus changes from false to true (not on every re-render)
    if (isFocused && !prevFocusedRef.current
      && searchTerm === editableSearchTerm
      && editableSearchTerm.length >= AUTOSUGGEST_THRESHOLD) {
      getSuggestions({ variables: { searchTerm: editableSearchTerm } });
    }

    prevFocusedRef.current = isFocused;
  }, [isFocused, searchTerm, editableSearchTerm, getSuggestions]);

  // Debounced call for when user is actively typing
  useDebounce(() => {
    if (isFocused
      && editableSearchTerm
      && editableSearchTerm.length >= AUTOSUGGEST_THRESHOLD
      && editableSearchTerm !== searchTerm) {
      getSuggestions({ variables: { searchTerm: editableSearchTerm } });
    }
  },
  SEARCH_FLYOUT_DEBOUNCE,
  [editableSearchTerm]);

  const keyDownHandler = (e) => {
    const { key } = e;

    const keyboardActions = {
      Escape: () => {
        blurHandler();
      },
      Enter: () => {
        blurHandler();
      },
    };
    keyboardActions[key]?.();
  };

  return {
    displayedData, keyDownHandler,
  };
}
